<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Accordion } from '#library/components.schema.build.ts'
	import { slide } from 'svelte/transition'
	import Render from '#components/render.svelte'
	import Image from '#components/image.svelte'
	import { Storyblok } from '#library/storyblok.svelte.ts'
	import Video from '#components/video.svelte'
	import * as _ from 'radashi'
	import { page } from '$app/state'
	import { SvelteSet } from 'svelte/reactivity'
	import { gen_id } from '#library/id.ts'
	import { scrollto } from '#library/scrollto.ts'

	interface Props {
		blok: Blok<Accordion> | Accordion
	}

	let { blok }: Props = $props()

	let initial = $state(true)

	// Default to the block's anchor or uid
	const anchor = $derived(blok.anchor || blok._uid!)

	// Default to the first item's title, but then allow users to collapse the first item by setting initial to false later
	const first = $derived(blok.items?.[0]?.title ? ref(blok.items[0]) : null)

	let active: string | null = $derived.by(() => {
		// If using hash to control the active item, then use that
		const hash = page.url.hash.slice(1).split('/')[1]
		const all_refs = new SvelteSet(blok.items?.map(ref) || [])
		if (all_refs.has(hash)) return hash

		// If using searchParams to control the active item, then use that
		const param = page.url.searchParams.get(anchor)
		if (param) return param

		// Initially set to the first item's title, but then allow users to collapse the first item by setting initial to false later
		if (initial) return first

		// Default to null
		return null
	})

	function ref(item: NonNullable<Accordion['items']>[number]): string {
		const title = typeof item.title === 'string' ? item.title : item.title?.text
		return gen_id(title) || ''
	}

	async function toggle(id: string) {
		initial = false
		active = active === id ? null : id
		scrollto(anchor + '/' + id)
	}
</script>

{#snippet Accordion()}
	{#each blok.items ?? [] as accord (accord._uid)}
		{@const id = ref(accord)}
		<div class="accord {blok.size}">
			{#if blok.items?.length && blok.items.length > 1}
				<button class="button {blok.size}" id="{anchor}/{id}" onclick={() => toggle(id)}>
					{accord.title}
					<svg class="arrow {blok.size}" class:active={active === id}>
						<use xlink:href="/images/vector.svg#chevron-up"></use>
					</svg>
				</button>
			{/if}
			{#if active === id}
				{#if 'blocks' in accord && accord.blocks?.length}
					<div class="content fill" transition:slide>
						{#each accord.blocks as item (item._uid)}
							<!--
                        TODO: This isn't passing theme correctly, but if apply a fix (below) then it breaks existing layouts.
                        <Storyblok.Block
                        blok={{ ...item, theme: 'theme' in item ? item.theme : blok.theme || 'theme-crocus-on-white' }}
                     /> -->
							<Storyblok.Block
								blok={{
									...item,
									theme: item.theme || blok.theme || 'theme-crocus-on-light-gimson'
								}}
							/>
						{/each}
					</div>
				{:else}
					<!-- TODO: We've definitely updated the content type to exclude Sidekick before, but I see it's back in the type definition. Matt need you to clarify as in the meantime, types not happy -->
					<div
						class="content"
						class:fill={!accord.body?.content?.[0].content}
						class:split={blok.split}
						transition:slide
					>
						<div class="assets" class:empty={!accord.images?.length && !accord.embed}>
							{#if accord.images?.length}
								<div class="images">
									{#each accord.images ?? [] as image (image.id)}
										{#if image?.filename}
											<Image src={image.filename} alt={accord.title} focus={image.focus} aspect="6/5" />
										{/if}
									{/each}
								</div>
							{/if}
							{#if accord.video?.m3u8_url}
								<Video video={accord.video} />
							{/if}
							{#if accord.embed}
								{@html accord.embed}
							{/if}
						</div>
						{#if accord.body?.content?.[0]?.content}
							<div class="body">
								<Render content={accord.body} />
							</div>
						{/if}
					</div>
				{/if}
			{/if}
		</div>
	{/each}
{/snippet}

{#if 'theme' in blok}
	<section class="accordion {blok.theme}" {...attrs(blok as Blok<Accordion>)}>
		<div class="contain" class:split={blok.split}>
			{#if blok.title?.text}
				<svelte:element
					this={blok.title.tag || 'div'}
					class="title"
					class:plain={blok.plain}
					class:split={blok.split}
				>
					{blok.title.text}
				</svelte:element>
			{/if}
			<div>
				{#if blok.body?.content?.[0]?.content}
					<div class="body" class:split={blok.split}>
						<Render content={blok.body} />
					</div>
				{/if}
				{@render Accordion()}
			</div>
		</div>
	</section>
{:else}
	<div class="accordion">
		<div class="contain">
			{@render Accordion()}
		</div>
	</div>
{/if}

<style lang="css">
	.accordion {
		background: var(--c-background);
		color: var(--c-midground);
		padding-block: var(--padding-top, var(--s-104)) var(--padding-bottom, var(--s-104));
	}

	.contain {
		display: grid;
		gap: 2.5rem;

		@media (--from-mobile) {
			&.split {
				grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
			}
		}
	}

	.title {
		color: var(--c-foreground);
		font: 600 var(--f-h2);
		letter-spacing: var(--ls-h2);
		max-width: 11ch;
		text-wrap: balance;

		&.plain {
			color: var(--c-midground);
		}
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-block-end: 2.5rem;

		:global(a) {
			color: var(--c-foreground);
			font-weight: 500;
		}
	}

	.accord {
		border-top: 1px solid hsl(0 0% 0% / 0.5);
		padding-block: 2.5rem;

		&.medium {
			padding-block: 2.25rem;
		}

		&.small {
			padding-block: 1.75rem;
		}
	}

	.button {
		align-items: center;
		display: flex;
		font: 600 var(--f-h3);
		justify-content: space-between;
		letter-spacing: var(--ls-h3);
		text-align: start;
		width: 100%;
		scroll-margin-block-start: 8rem;

		&.medium {
			font: 600 var(--f-h4);
			letter-spacing: var(--ls-h4);
		}

		&.small {
			font: 600 var(--f-h5);
			letter-spacing: var(--ls-h5);
		}
	}

	.arrow {
		flex-shrink: 0;
		rotate: 180deg;
		size: 3.5rem;
		transition: rotate 0.3s ease;

		&.medium {
			size: 2.75rem;
		}

		&.small {
			size: 1.75rem;
		}

		&.active {
			rotate: 0deg;
		}
	}

	.assets {
		@media (--from-mobile) {
			order: 1;
		}

		&.empty {
			@media (--until-mobile) {
				display: none;
			}
		}
	}

	.images {
		margin-inline: auto;
		max-width: 26rem;
	}

	.body {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);

		:global {
			.link {
				display: block;

				&:first-of-type {
					margin-block-start: 1rem;
				}
			}

			.anchor {
				max-width: none;
			}

			.button {
				margin-block-start: 2rem;
			}

			h4 {
				margin-block-start: 2rem;
			}
		}
	}

	.content {
		display: grid;
		gap: 2rem;
		padding-block-start: var(--s-24);

		@media (--from-mobile) {
			grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
		}

		:global(iframe) {
			aspect-ratio: 4 / 3;
		}

		@media (--from-mobile) {
			&.fill {
				grid-template-columns: unset;

				:global(iframe) {
					aspect-ratio: 21 / 9;
				}
			}

			&.split {
				grid-template-columns: unset;

				:global(iframe) {
					aspect-ratio: 16 / 9;
				}
			}
		}
	}
</style>
